import { AlertOctagon, Calendar } from "lucide-react";
import { renderMdInline } from "./markdown";

/**
 * "What happens if you skip the action plan" — sequential consequences of
 * inaction across 30 days / 90 days / 6 months / 12 months / 24 months.
 * Forces urgency around the Top Fixes by spelling out the cost of waiting.
 */
export default function ConsequenceFlowCard({ flow }) {
  if (!flow || flow.length === 0) return null;

  const sevStyle = (sev) => {
    if (sev === "critical") return {
      ring: "border-[#A84632]",
      bg: "bg-[#A84632]/8",
      text: "text-[#A84632]",
      dot: "bg-[#A84632]",
    };
    if (sev === "warning") return {
      ring: "border-[#E2A03F]",
      bg: "bg-[#E2A03F]/8",
      text: "text-[#9c6b1d]",
      dot: "bg-[#E2A03F]",
    };
    return {
      ring: "border-[#5C665D]/30",
      bg: "bg-[#F7F5F0]",
      text: "text-[#5C665D]",
      dot: "bg-[#5C665D]",
    };
  };

  return (
    <div className="mb-6" data-testid="consequence-flow-card">
      <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
        <div className="px-6 sm:px-8 py-5 border-b border-[#E5E0D8] flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-[#A84632]/10 flex items-center justify-center shrink-0">
            <AlertOctagon size={16} strokeWidth={1.75} className="text-[#A84632]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#A84632] font-medium">
              If you skip the action plan
            </p>
            <p className="font-serif-display text-xl sm:text-2xl text-[#1F382E] mt-0.5 tracking-tight leading-tight">
              What happens if these numbers stay the same.
            </p>
          </div>
        </div>

        <ol className="relative px-6 sm:px-8 py-6">
          {/* Vertical timeline line */}
          <div className="absolute left-[34px] sm:left-[42px] top-8 bottom-8 w-px bg-[#E5E0D8]" aria-hidden="true" />

          {flow.map((step, i) => {
            const s = sevStyle(step.severity);
            return (
              <li
                key={i}
                className="relative pl-8 sm:pl-10 pb-6 last:pb-0"
                data-testid={`consequence-step-${i}`}
              >
                {/* Timeline node */}
                <div
                  className={`absolute left-0 top-1 h-4 w-4 rounded-full border-2 ${s.ring} ${s.bg} flex items-center justify-center`}
                  aria-hidden="true"
                >
                  <div className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                </div>

                <div className="flex items-center gap-2 mb-1.5">
                  <Calendar size={11} strokeWidth={1.75} className={s.text} />
                  <p className={`text-[10px] uppercase tracking-[0.22em] font-medium ${s.text}`}>
                    {step.horizon}
                  </p>
                </div>
                <p
                  className="text-[14px] text-[#1A1A1A] leading-relaxed"
                  data-testid={`consequence-step-${i}-outcome`}
                >
                  {renderMdInline(step.outcome)}
                </p>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
